import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { usePrograms, useUpdateProgramStatus } from '../../services/programs';
import { cn } from '@/lib/utils';

interface ActivateSwapDialogProps {
  /** The program the member tried to activate. */
  programId: string;
  programName: string;
  /** The plan-limit message from the server (names the plan and the cap). */
  limitMessage: string;
  onClose: () => void;
  /** Called after the swap fully succeeds (old disabled, new active). */
  onSwapped?: () => void;
}

/**
 * Activate-at-the-cap escape hatch for programs — the sibling of the course
 * PublishSwapDialog (Aleix, 2026-09-08: "it caps at 2 but doesn't offer to
 * swap on the spot like courses do").
 *
 * Programs park as `disabled` rather than `draft`: `active → draft` is not a
 * legal transition, while `disabled → active` is, which is what makes the
 * rollback below possible.
 */
const ActivateSwapDialog: React.FC<ActivateSwapDialogProps> = ({
  programId,
  programName,
  limitMessage,
  onClose,
  onSwapped,
}) => {
  const { data } = usePrograms({ status: 'active', per_page: 50 });
  const active = (data?.programs ?? []).filter((p) => p.id !== programId);
  const updateStatus = useUpdateProgramStatus();
  const [picked, setPicked] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const swap = async () => {
    if (!picked) return;
    setWorking(true);
    try {
      await updateStatus.mutateAsync({ programId: picked, status: 'disabled' });
      try {
        await updateStatus.mutateAsync({ programId, status: 'active' });
      } catch (err) {
        // Roll the first half back — never leave the member with one program
        // silently switched off and nothing active in exchange.
        await updateStatus
          .mutateAsync({ programId: picked, status: 'active' })
          .catch(() => {});
        throw err;
      }
      toast.success(`Swapped — “${programName}” is now active.`);
      onSwapped?.();
      onClose();
    } catch {
      toast.error('The swap did not complete — nothing was changed.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !working) onClose();
      }}
    >
      <div className="flex w-full max-w-md flex-col gap-4 rounded-2xl border border-border-primary bg-surface-primary p-6">
        <h2 className="text-lg font-bold text-text-primary">Your plan is at its limit</h2>
        <p className="text-sm text-text-secondary">
          {limitMessage} To activate{' '}
          <span className="font-semibold text-text-primary">“{programName}”</span> now,
          choose an active program to switch off (its learners lose access until
          it&rsquo;s activated again — their progress is kept).
        </p>

        <div className="flex max-h-64 flex-col gap-2 overflow-y-auto pr-1">
          {active.map((p) => (
            <label
              key={p.id}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors duration-150',
                picked === p.id
                  ? 'border-primary-500/60 bg-primary-500/5 dark:bg-primary-350/10'
                  : 'border-border-primary hover:border-primary-300'
              )}
            >
              <input
                type="radio"
                name="swap-program"
                checked={picked === p.id}
                onChange={() => setPicked(p.id)}
                className="h-4 w-4 accent-primary-500"
              />
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
                {p.name}
              </span>
            </label>
          ))}
          {active.length === 0 && (
            <p className="text-sm text-text-tertiary">No other active programs found.</p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={working}
            className="rounded-full border border-text-primary/30 px-4 py-2 text-sm font-bold text-text-primary transition-[background-color,transform] duration-150 hover:bg-text-primary/[0.06] active:scale-[0.97]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={swap}
            disabled={!picked || working}
            className="rounded-full bg-primary-500 px-4 py-2 text-sm font-bold transition-[background-color,transform] duration-150 hover:bg-primary-600 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-60 dark:hover:bg-primary-400"
          >
            {working ? 'Swapping…' : 'Switch it off & activate'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivateSwapDialog;
